import React, { useState } from 'react';
import { X, Cloud, Shield, Clock, AlertTriangle } from 'lucide-react';
import { usePrivacyStore } from '../../store/usePrivacyStore';

export type PrivacyMode = 'cloud_sync' | 'incognito';

interface PrivacyModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentMode: PrivacyMode;
    onModeChange: (mode: PrivacyMode) => void;
}

const PrivacyModal: React.FC<PrivacyModalProps> = ({ isOpen, onClose, currentMode, onModeChange }) => {
    // Local state for UI switching before saving
    const [selectedMode, setSelectedMode] = useState<PrivacyMode>(currentMode);
    // US-12-01: first-time warning when picking incognito
    const warningDismissed = usePrivacyStore(s => s.warningDismissed);
    const dismissWarning = usePrivacyStore(s => s.dismissWarning);
    const [showWarning, setShowWarning] = useState(false);

    if (!isOpen) return null;

    const handleSave = () => {
        if (selectedMode === 'incognito' && !warningDismissed) {
            setShowWarning(true);
            return;
        }
        onModeChange(selectedMode);
        onClose();
    };

    const confirmIncognito = () => {
        dismissWarning();
        onModeChange('incognito');
        setShowWarning(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-black text-slate-800">Cài đặt Quyền riêng tư</h2>
                        <p className="text-xs text-slate-500 mt-1">Tùy chỉnh mức độ lưu trữ dựa trên độ nhạy cảm của tài liệu.</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    {/* Card 1: Cloud Sync */}
                    <label
                        className={`relative flex flex-col p-5 border-2 rounded-2xl cursor-pointer transition-all duration-200 ${selectedMode === 'cloud_sync'
                                ? 'border-indigo-500 bg-indigo-50/50 shadow-sm ring-4 ring-indigo-500/10'
                                : 'border-slate-200 bg-white hover:border-indigo-200 hover:bg-slate-50'
                            }`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3.5">
                                <div className={`p-2.5 rounded-xl ${selectedMode === 'cloud_sync' ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                                    <Cloud className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className={`text-base font-bold ${selectedMode === 'cloud_sync' ? 'text-indigo-900' : 'text-slate-700'}`}>
                                        Dịch Ngầm (Xóa sau khi mở)
                                    </h4>
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <Clock className="w-3.5 h-3.5 text-amber-500" />
                                        <span className="text-[10px] font-black text-amber-600 uppercase tracking-wide bg-amber-100/50 px-1.5 py-0.5 rounded">Xóa ngay khi đồng bộ</span>
                                    </div>
                                </div>
                            </div>
                            {/* Custom Radio */}
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 transition-colors ${selectedMode === 'cloud_sync' ? 'border-indigo-600' : 'border-slate-300'}`}>
                                {selectedMode === 'cloud_sync' && <div className="w-2.5 h-2.5 rounded-full bg-indigo-600 animate-in zoom-in" />}
                            </div>
                        </div>

                        <p className="mt-3.5 text-sm text-slate-600 leading-relaxed pl-14">
                            Hệ thống dịch nền tự động. Bạn <strong className="text-indigo-700">có thể tắt máy, đóng trình duyệt và mở lại sau để nhận file</strong>. Kết quả tạm cache trên server Dịch Tài Liệu (tối đa 48h) — sau khi bạn nhận xong, dữ liệu xóa vĩnh viễn. AI Gemini (Google Cloud) xử lý text theo <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="underline text-indigo-700 hover:text-indigo-800">Gemini Terms</a> — chi tiết tại <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-indigo-700 hover:text-indigo-800">Chính sách bảo mật</a>. <br/>
                            <span className="block mt-1.5 p-2 bg-indigo-50/50 rounded-lg text-xs border border-indigo-100/50 text-indigo-700/80">
                                <strong>* Giới hạn lưu trữ chờ:</strong> Tối đa 48 giờ. Nếu vượt quá thời gian này, file tự động tiêu hủy để bảo vệ an toàn.
                            </span>
                        </p>

                        <input
                            type="radio"
                            name="privacy_mode_modal"
                            value="cloud_sync"
                            className="sr-only"
                            checked={selectedMode === 'cloud_sync'}
                            onChange={() => setSelectedMode('cloud_sync')}
                        />
                    </label>

                    {/* Card 2: Incognito */}
                    <label
                        className={`relative flex flex-col p-5 border-2 rounded-2xl cursor-pointer transition-all duration-200 ${selectedMode === 'incognito'
                                ? 'border-fuchsia-500 bg-fuchsia-50/50 shadow-sm ring-4 ring-fuchsia-500/10'
                                : 'border-slate-200 bg-white hover:border-fuchsia-200 hover:bg-slate-50'
                            }`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3.5">
                                <div className={`p-2.5 rounded-xl ${selectedMode === 'incognito' ? 'bg-fuchsia-100 text-fuchsia-600' : 'bg-slate-100 text-slate-500'}`}>
                                    <Shield className="w-6 h-6" />
                                </div>
                                <div>
                                    <h4 className={`text-base font-bold ${selectedMode === 'incognito' ? 'text-fuchsia-900' : 'text-slate-700'}`}>
                                        Dịch Ẩn Danh (Skip cloud sync)
                                    </h4>
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <AlertTriangle className="w-3.5 h-3.5 text-rose-500" />
                                        <span className="text-[10px] font-black text-rose-600 uppercase tracking-wide bg-rose-100/50 px-1.5 py-0.5 rounded">Tiêu hủy lập tức</span>
                                    </div>
                                </div>
                            </div>
                            {/* Custom Radio */}
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-1 transition-colors ${selectedMode === 'incognito' ? 'border-fuchsia-600' : 'border-slate-300'}`}>
                                {selectedMode === 'incognito' && <div className="w-2.5 h-2.5 rounded-full bg-fuchsia-600 animate-in zoom-in" />}
                            </div>
                        </div>

                        <p className="mt-3.5 text-sm text-slate-600 leading-relaxed pl-14">
                            Server của Dịch Tài Liệu <strong className="text-fuchsia-700">KHÔNG lưu file</strong>. AI Gemini (Google Cloud) xử lý text theo <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="underline text-fuchsia-700 hover:text-fuchsia-800">Gemini Terms</a> rồi trả thẳng kết quả về máy bạn — chi tiết tại <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-fuchsia-700 hover:text-fuchsia-800">Chính sách bảo mật</a>.
                            <span className="block mt-1.5 p-2 bg-fuchsia-100/50 rounded-lg text-xs border border-fuchsia-100 text-fuchsia-700">
                                <strong>* Quan trọng: Bạn phải mở tab liên tục không được tắt</strong> trong suốt quá trình chờ dịch xong.
                            </span>
                        </p>

                        <input
                            type="radio"
                            name="privacy_mode_modal"
                            value="incognito"
                            className="sr-only"
                            checked={selectedMode === 'incognito'}
                            onChange={() => setSelectedMode('incognito')}
                        />
                    </label>
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                    <p className="text-[11px] text-slate-500 max-w-[200px] leading-tight">
                        Cấu hình này sẽ trở thành mặc định cho tất cả các bản dịch tiếp theo.
                    </p>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 font-bold text-sm text-slate-600 hover:text-slate-900 transition-colors"
                        >
                            Hủy
                        </button>
                        <button
                            onClick={handleSave}
                            className={`px-5 py-2 font-bold text-sm text-white rounded-xl shadow-lg transition-all active:scale-95 ${
                                selectedMode === 'incognito'
                                    ? 'bg-fuchsia-600 hover:bg-fuchsia-700 shadow-fuchsia-200' 
                                    : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200'
                            }`}
                        >
                            Lưu cấu hình
                        </button>
                    </div>
                </div>
            </div>

            {/* US-12-01: Warning modal first time choosing Incognito */}
            {showWarning && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="px-6 py-5 bg-gradient-to-r from-fuchsia-50 to-amber-50 border-b border-fuchsia-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-slate-800">Trước khi bật Ẩn Danh</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">Bạn cần hiểu rõ trade-off</p>
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-5 text-sm text-slate-700 space-y-3">
                            <p>✅ <strong>Được</strong>: Nội dung KHÔNG lưu server (pending_sync skip).</p>
                            <p>⚠️ <strong>Mất</strong>: Nếu <strong>sập máy / đóng tab</strong> giữa chừng dịch, bạn <strong>mất progress</strong> — không recover được.</p>
                            <p className="text-xs text-slate-500 italic">Với file &lt; 20 trang ít risk. File lớn nên dùng chế độ Cloud để an toàn.</p>
                        </div>
                        <div className="px-6 py-4 bg-slate-50 flex justify-end gap-2">
                            <button onClick={() => setShowWarning(false)} className="px-4 py-2 text-sm font-bold text-slate-600 hover:text-slate-900">
                                Hủy
                            </button>
                            <button onClick={confirmIncognito} className="px-4 py-2 text-sm font-bold text-white bg-fuchsia-600 hover:bg-fuchsia-700 rounded-xl shadow-lg">
                                Tôi hiểu, chọn Ẩn Danh
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrivacyModal;
